import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  formatLegalHeaderLine,
  getLegalDocument,
  legalSectionId,
  LEGAL_VERSION,
  renderLegalParagraph,
  type LegalDocId,
} from '@/lib/marketing/legal';
import { PageSeo } from '@/components/seo/PageSeo';
import { excerpt } from '@/lib/seo/excerpt';
import { INDEX_FOLLOW, NOINDEX_FOLLOW } from '@/lib/seo/constants';

const DOC_PATHS: Record<LegalDocId, string> = {
  privacy: '/privacy',
  terms: '/terms',
  cookies: '/cookies',
  subprocessors: '/subprocessors',
  'ai-transparency': '/ai-transparency',
};

export default function LegalDocumentPage({ docId }: { docId: LegalDocId }) {
  const { language } = useLanguage();
  const doc = getLegalDocument(docId, language === 'it' ? 'it' : 'en');

  const robots = docId === 'subprocessors' ? NOINDEX_FOLLOW : INDEX_FOLLOW;

  return (
    <div className="min-h-screen bg-brand-surface-light text-foreground flex flex-col">
      <PageSeo
        title={doc.metaTitle}
        description={excerpt(doc.intro)}
        path={DOC_PATHS[docId]}
        robots={robots}
      />
      <MarketingNav />
      <main className="flex-1 container py-10 md:py-14 max-w-3xl">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" asChild>
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            HiroMatch
          </Link>
        </Button>

        <header className="space-y-3 mb-10 pb-8 border-b border-border/60">
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{doc.title}</h1>
          <p className="text-sm text-muted-foreground">{formatLegalHeaderLine(language === 'it' ? 'it' : 'en')}</p>
          <p className="text-muted-foreground leading-relaxed">{doc.intro}</p>
        </header>

        <article className="prose prose-slate prose-sm md:prose-base max-w-none dark:prose-invert">
          {doc.sections.map((section) => (
            <section
              key={section.heading}
              id={legalSectionId(section.heading)}
              className="mb-10 not-prose scroll-mt-24"
            >
              <h2 className="font-display font-semibold text-xl mb-3 text-foreground">{section.heading}</h2>
              <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                {section.paragraphs.map((p, i) => renderLegalParagraph(p, `${section.heading}-${i}`))}
              </div>
              {section.table && (
                <div className="mt-5 overflow-x-auto rounded-md border border-border/60 bg-card">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <tr>
                        {section.table.columns.map((column) => (
                          <th key={column} scope="col" className="px-4 py-3 font-semibold">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {section.table.rows.map((row) => (
                        <tr key={row.join('|')}>
                          {row.map((cell, index) => (
                            <td key={`${cell}-${index}`} className="px-4 py-3 align-top text-muted-foreground">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}
        </article>

        <p className="mt-10 text-xs text-muted-foreground">
          {language === 'it' ? `Versione documento: ${LEGAL_VERSION}` : `Document version: ${LEGAL_VERSION}`}
        </p>

        <div className="mt-8 pt-8 border-t border-border/60 flex flex-wrap gap-4 text-sm">
          {(docId === 'privacy' || docId === 'terms') && (
            <Link to="/dpa" className="text-brand-accent hover:underline">
              {language === 'it' ? 'Accordo sul Trattamento dei Dati (DPA)' : 'Data Processing Agreement (DPA)'}
            </Link>
          )}
          {(Object.keys(DOC_PATHS) as LegalDocId[])
            .filter((id) => id !== docId && id !== 'subprocessors')
            .map((id) => {
              const other = getLegalDocument(id, language === 'it' ? 'it' : 'en');
              return (
                <Link key={id} to={DOC_PATHS[id]} className="text-brand-accent hover:underline">
                  {other.title}
                </Link>
              );
            })}
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
